import { ApiSearchSelect, DateSearchSelect, InputSearch, MultipleSearchSelect } from '@/components/custom/erp';
import type { InputTableCellParams, InputTableColumn, DataTableColumn } from '@/components/custom/erp';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';
import type { GiayBaoNo } from '@/types/schemas';
import { QUERY_KEYS } from '@/constants';

const STATUS_OPTIONS = [
  { value: '0', label: 'Lập chứng từ' },
  { value: '3', label: 'Chờ duyệt' },
  { value: '5', label: 'Chi tiền' },
  { value: '6', label: 'Khác' }
];

const MA_NGV_OPTIONS = [
  { value: '1', label: '1. Chi theo hóa đơn' },
  { value: '2', label: '2. Chi theo đối tượng' },
  { value: '3', label: '3. Chi khác' },
  { value: '4', label: '4. Rút tiền về nhập quỹ' },
  { value: '5', label: '5. Chuyển giữa các ngân hàng' },
  { value: '6', label: '6. Mua hàng chi tiền ngay' }
];

export const getDataTableColumns = (
  handleViewClick: (obj: GiayBaoNo) => void,
  totalAmount?: number | null
): DataTableColumn[] => [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    renderCell: params => {
      const status = params.value;
      const option = STATUS_OPTIONS.find(opt => opt.value === status);
      return option?.label || '';
    },
    renderSearchInput: params => <MultipleSearchSelect {...params} options={STATUS_OPTIONS} />
  },
  {
    field: 'so_ct',
    headerName: 'Số c/từ',
    width: 150,
    renderCell: params => (
      <button
        onClick={e => {
          e.stopPropagation();
          handleViewClick(params.row);
        }}
        className='hover:text-blue-500 hover:underline'
      >
        {params.row.so_ct}
      </button>
    )
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 120,
    renderCell: params => formatDate(params.row.ngay_ct),
    renderSearchInput: params => <DateSearchSelect {...params} />
  },
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 120,
    renderCell: params => params.row.ma_kh_data?.customer_code || params.row.ma_kh || '',
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='GIAY_BAO_NO'
        searchField='ma_kh'
        columns={[
          { key: 'customer_code', label: 'Mã khách hàng', width: '120px' },
          { key: 'customer_name', label: 'Tên khách hàng', width: '200px' },
          { key: 'tax_code', label: 'Diễn giải', width: '100px' }
        ]}
        valueKey='customer_code'
        displayKey='customer_code'
      />
    )
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 200,
    renderCell: params => params.row.ma_kh_data?.customer_name || params.row.ten_kh || '',
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='GIAY_BAO_NO'
        searchField='ma_kh'
        columns={[
          { key: 'customer_code', label: 'Mã khách hàng', width: '120px' },
          { key: 'customer_name', label: 'Tên khách hàng', width: '200px' },
          { key: 'tax_code', label: 'Diễn giải', width: '100px' }
        ]}
        valueKey='customer_name'
        displayKey='customer_name'
      />
    )
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200
  },
  {
    field: 'sten_tknh',
    headerName: 'Ngân hàng',
    width: 150,
    renderCell: params => params.row.tknh
  },
  {
    field: 'tk',
    headerName: 'Tk có',
    width: 100,
    renderCell: params => params.row.tk_data?.code || params.row.tk || '',
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='GIAY_BAO_NO'
        searchField='tk'
        columns={[
          { key: 'code', label: 'Mã tài khoản', width: '120px' },
          { key: 'name', label: 'Tên tài khoản', width: '200px' }
        ]}
        valueKey='code'
        displayKey='name'
      />
    )
  },
  {
    field: 't_tien_nt',
    headerName: 'Tổng thanh toán',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber(params.value),
    renderSearchInput: params => (
      <InputSearch
        {...params}
        onKeyDown={params.onKeyDown ?? (() => {})}
        suffixContent={
          <div className='flex items-center gap-2'>
            <span className='text-lg font-bold'>&#931;</span>
            <span className='font-semibold text-main'>
              {typeof totalAmount === 'number' ? formatRoundedNumber(totalAmount) : '—'}
            </span>
          </div>
        }
      />
    )
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 120,
    align: 'center',
    renderCell: params => params.row.ma_nt_data?.ma_nt || params.row.ma_nt || ''
  },
  {
    field: 'ma_ngv',
    headerName: 'Loại chứng từ',
    width: 200,
    renderCell: params => {
      const status = params.row.ma_ngv;
      const option = MA_NGV_OPTIONS.find(opt => opt.value === status);
      return option?.label || '';
    },
    renderSearchInput: params => <MultipleSearchSelect {...params} options={MA_NGV_OPTIONS} />
  }
];

export const getInputTableColumns = (): InputTableColumn[] => {
  return [
    {
      field: 'ma_kh',
      headerName: 'Mã đối tượng',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.ma_kh_data?.customer_code
    },
    {
      field: 'ten_kh',
      headerName: 'Tên đối tượng',
      width: 200,
      renderCell: (params: InputTableCellParams) => params.row.ma_kh_data?.customer_name
    },
    {
      field: 'du_cn',
      headerName: 'Dư công nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.du_cn
    },
    {
      field: 'id_hd',
      headerName: 'Hóa đơn',
      width: 200,
      renderCell: (params: InputTableCellParams) => params.row.id_hd_data?.so_ct
    },
    {
      field: 'ngay_hd',
      headerName: 'Ngày hóa đơn',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.id_hd_data?.ngay_ct
    },
    {
      field: 'tk_no',
      headerName: 'Tài khoản nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.tk_no_data?.code
    },
    {
      field: 'ten_tk',
      headerName: 'Tên tài khoản',
      width: 200,
      renderCell: (params: InputTableCellParams) => params.row.tk_no_data?.name
    },
    {
      field: 'ma_nt',
      headerName: 'Ngoại tệ',
      width: 100,
      renderCell: (params: InputTableCellParams) => params.row.id_hd_data?.ngoai_te
    },
    {
      field: 'ty_gia_hd',
      headerName: 'Tỷ giá hđ',
      width: 100,
      type: 'number',
      align: 'right',
      renderCell: (params: InputTableCellParams) => params.row.id_hd_data?.ty_gia_hd
    },
    {
      field: 'da_pb_nt',
      headerName: 'Đã phân bổ',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params: InputTableCellParams) =>
        params.row.id_hd_data?.da_thanh_toan ?? params.row.id_hd_data?.da_pb_nt
    },
    {
      field: 'cl_nt',
      headerName: 'Còn lại',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params: InputTableCellParams) => params.row.id_hd_data?.cl_nt
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 150,
      type: 'number',
      align: 'right',
      renderCell: (params: InputTableCellParams) => formatMoney(params.row.tien_nt)
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải',
      width: 300,
      renderCell: (params: InputTableCellParams) => params.row.dien_giai
    },
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.ma_bp_data?.ma_bp
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.ma_vv_data?.ma_vu_viec
    },
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.ma_hd_data?.ma_hd
    },
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 150,
      renderCell: (params: InputTableCellParams) => params.row.ma_dtt_data?.ma_dtt
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.ma_ku_data?.ma_ku
    },
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.ma_phi_data?.ma_phi
    },
    {
      field: 'ma_sp',
      headerName: 'Sản phẩm',
      width: 120,
      renderCell: (params: InputTableCellParams) => params.row.ma_sp_data?.ma_vt
    },
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 180,
      renderCell: (params: InputTableCellParams) => params.row.ma_cp0_data?.ma_cpkhl
    },
    {
      field: 'ma_loai_hd',
      headerName: 'Loại hóa đơn',
      width: 120
    },
    {
      field: 'ma_thue',
      headerName: 'Thuế suất',
      width: 100
    },
    {
      field: 'ma_mau_bc',
      headerName: 'Mẫu báo cáo',
      width: 120
    },
    {
      field: 'ma_tc_thue',
      headerName: 'Mã tính chất',
      width: 120
    }
  ];
};

export const historyColumns: InputTableColumn[] = [
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
];
